/**
 * AdminActivityTable — WCO-3286 general admin audit log viewer.
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useLanguage } from '../../../i18n/LanguageContext';
import { RefreshCw, ChevronLeft, ChevronRight, Filter, X, Calendar } from 'lucide-react';
import DatePickerDropdown from '../../../components/DatePickerDropdown';
import api from '../../../services/api';
import AdminActivityStateDiff from './AdminActivityStateDiff';
import { formatPickerDateLabel } from './adminActivityStateDiffUtils';

export interface AdminAuditRecord {
  id: number;
  actor_user_id: string | null;
  actor_name: string | null;
  entity: string;
  entity_id: string;
  action: string;
  before_state: Record<string, unknown> | null;
  after_state: Record<string, unknown> | null;
  occurred_at: string;
}

const ENTITY_OPTIONS = [
  'config_setting',
  'packing_threshold',
  'user',
  'station',
  'camera',
  'byproduct_mapping',
  'input_output_mapping',
  'packing_mapping',
];

const FILTER_CONTROL_CLASS =
  'w-full text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500';

// Fixed width on the trigger itself, not w-full: DatePickerDropdown wraps children in an
// `inline-block`, so a w-full trigger shrink-wraps its text and the box resizes as soon as a
// date is picked. w-60 also keeps the calendar icon off the year.
const DATE_PICKER_TRIGGER_CLASS =
  'w-60 flex items-center justify-between gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 text-left';

// Table cell classes — same strings the sibling Reports tabs use (ProductionRecords), so the rows
// read with the app's text colour in both themes instead of the browser default.
const TH_CLASS =
  'px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap';
const TD_CLASS = 'px-4 py-3 text-sm text-gray-700 dark:text-gray-300';

interface AdminActivityTableProps {
  embedded?: boolean;
}

const AdminActivityTable: React.FC<AdminActivityTableProps> = ({ embedded = false }) => {
  const { t } = useLanguage();
  const [records, setRecords] = useState<AdminAuditRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 50;

  const [entityFilter, setEntityFilter] = useState('');
  const [actorFilter, setActorFilter] = useState('');
  const [debouncedActorFilter, setDebouncedActorFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const skipActorDebounceOnMount = useRef(true);

  useEffect(() => {
    if (skipActorDebounceOnMount.current) {
      skipActorDebounceOnMount.current = false;
      return;
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedActorFilter(actorFilter);
      setPage(1);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [actorFilter]);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params: Record<string, string | number> = { page, limit };
      if (entityFilter) params.entity = entityFilter;
      if (debouncedActorFilter.trim()) params.actor = debouncedActorFilter.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const { data } = await api.get('/admin-audit-log', { params });
      setRecords(data.records || []);
      setTotalPages(data.pagination?.pages || 0);
      setTotal(data.pagination?.total || 0);
    } catch {
      setRecords([]);
      setTotalPages(0);
      setTotal(0);
      setLoadError(t('adminActivity.loadError'));
    } finally {
      setLoading(false);
    }
  }, [page, entityFilter, debouncedActorFilter, dateFrom, dateTo, t]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const clearFilters = () => {
    setEntityFilter('');
    setActorFilter('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const entityLabel = (entity: string) =>
    t(`adminActivity.entities.${entity}`) || entity;

  const actionLabel = (action: string) =>
    t(`adminActivity.actions.${action}`) || action;

  const interpolate = (template: string, vars: Record<string, string | number>) =>
    Object.entries(vars).reduce(
      (str, [key, val]) => str.replace(new RegExp(`\\{${key}\\}`, 'g'), String(val)),
      template
    );

  return (
    <div className={embedded ? 'space-y-4' : 'space-y-6'}>
      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        {/* Single row: the "Filters" title sits beside the controls (labels + inputs), so the
            section is two lines instead of three and the date fields keep their natural width. */}
        <div className="flex flex-col md:flex-row md:flex-wrap md:items-end gap-3">
          {/* Title sits on the label row (self-start), not down beside the inputs. */}
          <div className="flex items-center gap-2 shrink-0 md:self-start">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('adminActivity.filters')}
            </span>
          </div>
          {/* Fixed width, sized to the longest option ("Input/Output Mapping") rather than
              stretching across the row. */}
          <div className="md:w-56 shrink-0">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('adminActivity.entityFilter')}</label>
            <select
              value={entityFilter}
              onChange={(e) => { setEntityFilter(e.target.value); setPage(1); }}
              className={FILTER_CONTROL_CLASS}
            >
              <option value="">{t('adminActivity.allEntities')}</option>
              {ENTITY_OPTIONS.map((e) => (
                <option key={e} value={e}>{entityLabel(e)}</option>
              ))}
            </select>
          </div>
          <div className="md:w-64 shrink-0">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('adminActivity.actorFilter')}</label>
            <input
              type="text"
              value={actorFilter}
              onChange={(e) => { setActorFilter(e.target.value); setPage(1); }}
              placeholder={t('adminActivity.actorPlaceholder')}
              className={`${FILTER_CONTROL_CLASS} placeholder:text-gray-400 dark:placeholder:text-gray-500`}
            />
          </div>
          <div className="shrink-0">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('adminActivity.dateFrom')}</label>
            <DatePickerDropdown
              selected={dateFrom || null}
              onSelect={(date) => { setDateFrom(date); setPage(1); }}
            >
              {({ onClick }) => (
                <button type="button" onClick={onClick} className={DATE_PICKER_TRIGGER_CLASS}>
                  <span className="whitespace-nowrap">{dateFrom ? formatPickerDateLabel(dateFrom) : t('adminActivity.selectDate')}</span>
                  <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
                </button>
              )}
            </DatePickerDropdown>
          </div>
          <div className="shrink-0">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('adminActivity.dateTo')}</label>
            <DatePickerDropdown
              selected={dateTo || null}
              onSelect={(date) => { setDateTo(date); setPage(1); }}
            >
              {({ onClick }) => (
                <button type="button" onClick={onClick} className={DATE_PICKER_TRIGGER_CLASS}>
                  <span className="whitespace-nowrap">{dateTo ? formatPickerDateLabel(dateTo) : t('adminActivity.selectDate')}</span>
                  <Calendar className="w-4 h-4 text-gray-400 shrink-0" />
                </button>
              )}
            </DatePickerDropdown>
          </div>
          {(entityFilter || actorFilter || dateFrom || dateTo) && (
            <button
              onClick={clearFilters}
              className="text-xs text-primary-600 hover:underline flex items-center gap-1 shrink-0 md:self-end md:pb-2"
            >
              <X className="w-3 h-3" />
              {t('adminActivity.clearFilters')}
            </button>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {interpolate(t('adminActivity.recordCount'), { count: total })}
        </p>
        <button
          onClick={fetchRecords}
          disabled={loading}
          className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {t('common.refresh')}
        </button>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className={TH_CLASS}>{t('adminActivity.when')}</th>
                <th className={TH_CLASS}>{t('adminActivity.who')}</th>
                <th className={TH_CLASS}>{t('adminActivity.entity')}</th>
                <th className={TH_CLASS}>{t('adminActivity.entityId')}</th>
                <th className={TH_CLASS}>{t('adminActivity.action')}</th>
                <th className={TH_CLASS}>{t('adminActivity.details')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading && records.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    {t('common.loading')}
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                    {loadError || t('adminActivity.noRecords')}
                  </td>
                </tr>
              ) : (
                records.map((row) => (
                  <React.Fragment key={row.id}>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                      <td className={`${TD_CLASS} whitespace-nowrap`}>
                        {new Date(row.occurred_at).toLocaleString()}
                      </td>
                      <td className={TD_CLASS}>
                        <div>{row.actor_name || row.actor_user_id || '—'}</div>
                        {row.actor_user_id && row.actor_name !== row.actor_user_id && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">{row.actor_user_id}</div>
                        )}
                      </td>
                      <td className={TD_CLASS}>{entityLabel(row.entity)}</td>
                      <td className={`${TD_CLASS} font-mono text-xs`}>{row.entity_id}</td>
                      <td className={TD_CLASS}>
                        <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                          {actionLabel(row.action)}
                        </span>
                      </td>
                      <td className={TD_CLASS}>
                        <button
                          onClick={() => setExpandedId(expandedId === row.id ? null : row.id)}
                          className="text-primary-600 hover:underline text-xs"
                        >
                          {expandedId === row.id ? t('adminActivity.hideDetails') : t('adminActivity.showDetails')}
                        </button>
                      </td>
                    </tr>
                    {expandedId === row.id && (
                      <tr>
                        <td colSpan={6} className="px-4 py-3 bg-gray-50 dark:bg-gray-900">
                          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                            {t('adminActivity.stateChanges')}
                          </p>
                          <AdminActivityStateDiff
                            before={row.before_state}
                            after={row.after_state}
                            t={t}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="flex items-center gap-1 text-sm disabled:opacity-50"
            >
              <ChevronLeft className="w-4 h-4" />
              {t('common.previous')}
            </button>
            <span className="text-sm text-gray-500">
              {interpolate(t('adminActivity.pageOf'), { page, pages: totalPages })}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="flex items-center gap-1 text-sm disabled:opacity-50"
            >
              {t('common.next')}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminActivityTable;
